import { motion } from 'framer-motion';
import { useState } from 'react';
import { FaWhatsapp, FaPhone, FaEnvelope, FaCheckCircle, FaStar } from 'react-icons/fa';
import PageTransition from '../components/common/PageTransition';
import { CONTACT } from '../utils/constants';
import SEO from '../components/common/SEO';

const editions = [
  { id: 'micro', name: 'Micro Edition', description: 'Perfect for ages 4–9 years', icon: '🌈', color: '#7B2FBE', bg: 'rgba(123,47,190,0.08)', border: 'rgba(123,47,190,0.2)' },
  { id: 'macro', name: 'Macro Edition', description: 'Designed for ages 10–15 years', icon: '🚀', color: '#1565C0', bg: 'rgba(21,101,192,0.08)', border: 'rgba(21,101,192,0.2)' },
  { id: 'both', name: 'Both Editions', description: 'Great for families with multiple kids', icon: '👨‍👩‍👧‍👦', color: '#3CB044', bg: 'rgba(60,176,68,0.08)', border: 'rgba(60,176,68,0.2)', badge: 'Best Value! 🎯' },
];

const plans = [
  { id: 'monthly', name: 'Monthly', duration: '1 Month', icon: '📅', popular: false, color: '#7B2FBE',
    features: ['Weekly issues', 'Full access to content', 'Puzzles & activities', 'Stories & comics', 'Cancel anytime'] },
  { id: 'yearly', name: 'Yearly', duration: '12 Months', icon: '🏆', popular: true, color: '#1565C0',
    features: ['35 weekly issues', 'Full access to content', 'Puzzles & activities', 'Stories & comics', 'Free bonus issues', 'Priority support', 'Exclusive contests'] },
  { id: 'halfyearly', name: 'Half-Yearly', duration: '6 Months', icon: '⭐', popular: false, color: '#3CB044',
    features: ['Weekly issues', 'Full access to content', 'Puzzles & activities', 'Stories & comics', 'Flexible commitment'] },
];

const benefits = [
  { icon: '📚', title: 'Weekly Fresh Content', description: 'New stories, puzzles, and activities every single week to keep learning exciting!', color: '#7B2FBE' },
  { icon: '🧠', title: 'Educational & Fun', description: 'Content designed by educators that makes learning feel like play.', color: '#1565C0' },
  { icon: '🎨', title: 'Creative Activities', description: 'Art, crafts, and DIY projects to spark creativity and imagination.', color: '#F57C00' },
  { icon: '🏆', title: 'Contests & Prizes', description: 'Regular competitions where kids can win exciting prizes and certificates!', color: '#D32F2F' },
  { icon: '👨‍👩‍👧‍👦', title: 'Family Bonding', description: 'Activities that bring families together for quality time.', color: '#3CB044' },
  { icon: '📱', title: 'Easy to Subscribe', description: 'Quick signup via WhatsApp. Hassle-free delivery to your doorstep!', color: '#00897B' },
];

const testimonials = [
  { quote: "My kids wait eagerly for The Tiny Times every week! It's become our family reading tradition.", name: 'Priya Sharma', role: 'Mother of 2', color: '#7B2FBE' },
  { quote: "The puzzles and activities keep my daughter engaged for hours. Best investment for her learning!", name: 'Rahul Mehta', role: 'Father', color: '#1565C0' },
  { quote: "As a teacher, I recommend The Tiny Times to all parents. The content quality is exceptional.", name: 'Mrs. Lakshmi', role: 'School Teacher', color: '#3CB044' },
];

const faqs = [
  { question: 'How do I subscribe to The Tiny Times?', answer: 'Simply click the WhatsApp button on this page, and our team will guide you through the subscription process. It takes less than 2 minutes!' },
  { question: 'How will I receive the magazine?', answer: 'The magazine is delivered to your doorstep every week. We also provide digital copies for convenience.' },
  { question: 'Can I choose between Micro and Macro editions?', answer: 'Yes! Micro Edition is for ages 4–9, and Macro Edition is for ages 10–15. You can also subscribe to both editions if you have children in different age groups.' },
  { question: 'What if I want to cancel my subscription?', answer: 'You can cancel anytime by contacting us via WhatsApp or email. We offer hassle-free cancellations.' },
  { question: 'Are there any discounts for long-term subscriptions?', answer: 'Yes! Our yearly plan offers the best value with bonus issues and exclusive content. Contact us for current pricing.' },
  { question: 'Is the content available in other languages?', answer: 'Our magazine features English content with Telugu language elements to support multilingual learning.' },
];

const FAQItem = ({ question, answer, index }) => {
  const [isOpen, setIsOpen] = useState(false);
  const colors = ['#7B2FBE', '#1565C0', '#3CB044', '#F57C00', '#D32F2F', '#00897B'];
  const color = colors[index % colors.length];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="rounded-2xl overflow-hidden"
      style={{ background: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-left p-5 md:p-6 gap-4"
      >
        <h3 className="text-base md:text-lg font-display font-bold" style={{ color: '#1A1F2E' }}>
          {question}
        </h3>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: isOpen ? color : 'rgba(0,0,0,0.05)', color: isOpen ? 'white' : '#616E7A' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      </button>

      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <p className="px-5 md:px-6 pb-5 text-neutral-gray-dark leading-relaxed border-t border-gray-100 pt-4 text-sm md:text-base">
          {answer}
        </p>
      </motion.div>
    </motion.div>
  );
};

const Subscribe = () => {
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [selectedEdition, setSelectedEdition] = useState('both');

  const getWhatsAppMessage = () => {
    const planLabel = plans.find(p => p.id === selectedPlan)?.name || 'Yearly';
    const editionLabel = editions.find(e => e.id === selectedEdition)?.name || 'Both Editions';
    return encodeURIComponent(
      `Hi! I would like to subscribe to The Tiny Times.\n\nPlan: ${planLabel}\nEdition: ${editionLabel}\n\nPlease share the details.`
    );
  };

  const whatsappLink = `https://wa.me/919618377750?text=${getWhatsAppMessage()}`;

  return (
    <PageTransition>
      <SEO
        title="Subscribe Now - Choose Plan & Edition"
        description="Get The Tiny Times delivered directly to you every week. Choose your subscription plan for Junior or Senior editions and start the learning adventure!"
        keywords="subscribe children magazine, kids weekly subscription price, buy activity book kids, order kids magazine online"
      />
      <div className="min-h-screen">

        {/* Hero */}
        <section className="relative py-24 md:py-36 overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #E8F5E9 0%, #EFF6FF 45%, #F3EEFF 100%)' }}>
          <div className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle, rgba(60,176,68,0.15) 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
          <div className="absolute top-0 right-0 w-80 h-80 opacity-20 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(60,176,68,0.35), transparent 70%)', filter: 'blur(60px)', transform: 'translate(20%,-20%)' }} />

          {['📬', '🎉', '⭐', '📚', '🚀'].map((emoji, i) => (
            <motion.div key={i} className="absolute text-5xl select-none pointer-events-none opacity-15"
              style={{ left: `${8 + i * 20}%`, top: `${20 + (i % 3) * 25}%` }}
              animate={{ y: [0, -25, 0], rotate: [0, 10, -10, 0] }}
              transition={{ duration: 3 + i, repeat: Infinity, delay: i * 0.5 }}>
              {emoji}
            </motion.div>
          ))}

          <div className="container mx-auto px-4 relative z-10">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
                className="text-8xl mb-6">🎊</motion.div>
              <h1 className="text-5xl md:text-7xl font-display font-bold mb-5 leading-tight" style={{ color: '#1A1F2E' }}>
                Subscribe{' '}
                <span style={{ background: 'linear-gradient(135deg, #3CB044, #1565C0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Now!
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-neutral-gray-dark mb-8 max-w-xl mx-auto leading-relaxed">
                Join thousands of happy families! Weekly stories, puzzles & fun learning delivered to your doorstep 📬
              </p>
              <motion.a
                href={CONTACT.whatsappLink} target="_blank" rel="noopener noreferrer"
                whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.96 }}
                className="inline-flex items-center gap-3 px-10 py-5 rounded-button font-display font-bold text-2xl text-white"
                style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)', boxShadow: '0 8px 32px rgba(37,211,102,0.4)' }}>
                <FaWhatsapp className="text-3xl" />
                Subscribe via WhatsApp
              </motion.a>
              <p className="text-neutral-gray-dark mt-4 text-sm">Or scroll down to choose your plan ↓</p>
            </motion.div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
            <svg viewBox="0 0 1440 80" className="w-full" preserveAspectRatio="none" style={{ display: 'block' }}>
              <path fill="white" d="M0,40L60,45C120,50,240,60,360,55C480,50,600,30,720,28C840,25,960,35,1080,40C1200,45,1320,42,1380,40L1440,38L1440,80L0,80Z" />
            </svg>
          </div>
        </section>

        {/* Edition Selection */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>Choose Your Edition 📚</h2>
              <p className="text-lg text-neutral-gray-dark max-w-xl mx-auto">Select the perfect edition for your child's age group</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {editions.map((edition, index) => (
                <motion.div
                  key={edition.id}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }} whileHover={{ y: -6 }}
                  onClick={() => setSelectedEdition(edition.id)}
                  className="relative rounded-2xl p-6 text-center cursor-pointer transition-all"
                  style={{
                    background: selectedEdition === edition.id ? edition.bg : 'white',
                    border: `2px solid ${selectedEdition === edition.id ? edition.border : 'rgba(0,0,0,0.06)'}`,
                    boxShadow: selectedEdition === edition.id ? `0 8px 32px ${edition.bg}` : '0 4px 20px rgba(0,0,0,0.05)',
                    outline: selectedEdition === edition.id ? `2px solid ${edition.color}` : 'none',
                    outlineOffset: '0px',
                  }}
                >
                  {selectedEdition === edition.id && (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                      className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ background: edition.color }}>
                      <FaCheckCircle className="text-white text-sm" />
                    </motion.div>
                  )}
                  <div className="text-6xl mb-4">{edition.icon}</div>
                  <h3 className="text-xl font-display font-bold mb-2" style={{ color: edition.color }}>{edition.name}</h3>
                  <p className="text-sm text-neutral-gray-dark mb-3">{edition.description}</p>
                  {edition.badge && (
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-display font-bold text-white"
                      style={{ background: '#F57C00' }}>{edition.badge}</span>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Plans */}
        <section className="py-16 md:py-24" style={{ background: 'linear-gradient(160deg, #F0FFF4, #EFF6FF, #F5F0FF)' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>Choose Your Plan 📋</h2>
              <p className="text-lg text-neutral-gray-dark max-w-xl mx-auto">Flexible subscription options to suit your family's needs</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {plans.map((plan, index) => (
                <motion.div key={plan.id}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }} whileHover={{ y: -10 }}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`relative cursor-pointer ${plan.popular ? 'md:-mt-4' : ''}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-0 right-0 flex justify-center z-10">
                      <div className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-display font-bold text-white"
                        style={{ background: plan.color, boxShadow: `0 4px 16px ${plan.color}50` }}>
                        <FaStar /> Most Popular
                      </div>
                    </div>
                  )}
                  <div className="rounded-2xl p-6 h-full transition-all"
                    style={{
                      background: selectedPlan === plan.id ? `rgba(${plan.id === 'monthly' ? '123,47,190' : plan.id === 'yearly' ? '21,101,192' : '60,176,68'},0.07)` : 'white',
                      border: `2px solid ${selectedPlan === plan.id ? plan.color : 'rgba(0,0,0,0.06)'}`,
                      boxShadow: plan.popular ? `0 12px 40px ${plan.color}25` : '0 4px 20px rgba(0,0,0,0.05)',
                    }}>
                    <div className="text-center mb-5">
                      <div className="text-5xl mb-3">{plan.icon}</div>
                      <h3 className="text-2xl font-display font-bold" style={{ color: plan.color }}>{plan.name}</h3>
                      <p className="text-sm text-neutral-gray-dark">{plan.duration}</p>
                    </div>
                    <div className="text-center mb-5 py-4 rounded-xl" style={{ background: `${plan.color}10` }}>
                      <div className="text-2xl font-display font-bold" style={{ color: plan.color }}>Contact Us for Pricing</div>
                      <div className="text-xs text-neutral-gray-dark mt-1">Reach out via WhatsApp 💬</div>
                    </div>
                    <ul className="space-y-2.5 mb-5">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2.5">
                          <FaCheckCircle className="flex-shrink-0 mt-0.5 text-sm" style={{ color: plan.color }} />
                          <span className="text-sm text-neutral-gray-dark">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      className="w-full py-2.5 rounded-xl font-display font-semibold text-sm transition-all"
                      style={{
                        background: selectedPlan === plan.id ? plan.color : `${plan.color}15`,
                        color: selectedPlan === plan.id ? 'white' : plan.color,
                      }}
                    >
                      {selectedPlan === plan.id ? '✓ Selected' : 'Select Plan'}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Subscribe CTA */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto">
              <div className="rounded-[2.5rem] p-8 md:p-12 text-center relative overflow-hidden border border-white/60"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.4)', 
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.08)' 
                }}>
                <div className="absolute inset-0 bg-gradient-to-br from-green-400/10 via-blue-400/10 to-purple-400/10 pointer-events-none"></div>
                <div className="text-7xl mb-4 relative z-10">🎯</div>
                <h2 className="text-3xl md:text-5xl font-display font-bold mb-3 relative z-10" style={{ color: '#1A1F2E' }}>Ready to Subscribe?</h2>
                <p className="text-neutral-gray-dark mb-10 text-lg md:text-xl relative z-10">
                  You've selected: <strong className="text-primary-green-dark">{editions.find(e => e.id === selectedEdition)?.name}</strong> with <strong className="text-primary-blue-dark">{plans.find(p => p.id === selectedPlan)?.name}</strong> plan
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 relative z-10">
                  <motion.a href={whatsappLink} target="_blank" rel="noopener noreferrer"
                    whileHover={{ scale: 1.04, y: -4 }} whileTap={{ scale: 0.97 }}
                    className="flex flex-col items-center gap-2 p-5 rounded-2xl text-white"
                    style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)', boxShadow: '0 8px 32px rgba(37,211,102,0.3)' }}>
                    <FaWhatsapp className="text-4xl" />
                    <div className="font-display font-bold">WhatsApp</div>
                    <div className="text-xs text-white/90">Instant Response</div>
                  </motion.a>
                  <motion.a href={`tel:${CONTACT.phone}`} whileHover={{ scale: 1.04, y: -4 }} whileTap={{ scale: 0.97 }}
                    className="flex flex-col items-center gap-2 p-5 rounded-2xl transition-all" style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.9)', color: '#1A1F2E' }}>
                    <FaPhone className="text-4xl" style={{ color: '#1565C0' }} />
                    <div className="font-display font-bold">Call Us</div>
                    <div className="text-xs text-neutral-gray-dark">{CONTACT.phone}</div>
                  </motion.a>
                  <motion.a href={`mailto:${CONTACT.email}?subject=Subscription Inquiry`} whileHover={{ scale: 1.04, y: -4 }} whileTap={{ scale: 0.97 }}
                    className="flex flex-col items-center gap-2 p-5 rounded-2xl transition-all" style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.9)', color: '#1A1F2E' }}>
                    <FaEnvelope className="text-4xl" style={{ color: '#F57C00' }} />
                    <div className="font-display font-bold">Email</div>
                    <div className="text-xs text-neutral-gray-dark">Write to us</div>
                  </motion.a>
                </div>
                <motion.a href={whatsappLink} target="_blank" rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  className="relative z-10 flex flex-col md:flex-row items-center justify-center gap-3 w-full py-4 md:py-5 rounded-button font-display font-bold text-xl md:text-2xl text-white"
                  style={{ background: 'linear-gradient(135deg, #1E7C24, #145218)', boxShadow: '0 10px 30px rgba(30,124,36,0.3)' }}>
                  <FaWhatsapp className="text-3xl text-white" />
                  <span>Subscribe Now via WhatsApp</span>
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 md:py-24" style={{ background: 'linear-gradient(160deg, #FFF8E1, #E3F2FD)' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>Why Subscribe? 🌟</h2>
              <p className="text-lg text-neutral-gray-dark max-w-xl mx-auto">Join thousands of happy families enjoying quality content every week!</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {benefits.map((benefit, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }} whileHover={{ y: -8 }}
                  className="rounded-2xl p-6 text-center bg-white" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                  <div className="text-6xl mb-4">{benefit.icon}</div>
                  <h3 className="text-xl font-display font-bold mb-2" style={{ color: benefit.color }}>{benefit.title}</h3>
                  <p className="text-sm text-neutral-gray-dark leading-relaxed">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>What Parents Say 💬</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }} whileHover={{ y: -6 }}
                  className="rounded-2xl p-7" style={{ background: `${testimonial.color}08`, border: `1px solid ${testimonial.color}20`, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => <FaStar key={i} style={{ color: '#F9C513' }} className="text-lg" />)}
                  </div>
                  <p className="text-neutral-gray-dark leading-relaxed mb-6 italic">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-display font-bold"
                      style={{ background: testimonial.color }}>
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-display font-bold text-sm" style={{ color: testimonial.color }}>{testimonial.name}</div>
                      <div className="text-xs text-neutral-gray-dark">{testimonial.role}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16 md:py-24" style={{ background: 'linear-gradient(160deg, #F0FFF4, #EFF6FF)' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>Frequently Asked Questions ❓</h2>
            </motion.div>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, index) => (
                <FAQItem key={index} question={faq.question} answer={faq.answer} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20" style={{ background: 'linear-gradient(135deg, #F57C00, #FF9800, #FFC107)' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center">
              <motion.div animate={{ rotate: [0, 10, -10, 10, 0], scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }} className="text-8xl mb-5">🎁</motion.div>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4 drop-shadow">Limited Time Offer!</h2>
              <p className="text-xl text-white/90 mb-8">Subscribe Today To Get An Amazing Offer 🎉</p>
              <motion.a href={CONTACT.whatsappLink} target="_blank" rel="noopener noreferrer"
                whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.96 }}
                className="inline-flex items-center gap-3 px-10 py-5 rounded-button font-display font-bold text-2xl"
                style={{ background: 'white', color: '#F57C00', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                <FaWhatsapp className="text-3xl text-[#25D366]" />
                Subscribe Now
              </motion.a>
            </motion.div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

export default Subscribe;